import { useState, FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { loginUser } from "../../store/slices/authentificationSlice";
import { encoursowner } from "../../store/slices/ownerSlice";
import { User, Lock } from "lucide-react";

import { useAppDispatch, useAuth } from "../../store/hook";

const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, loading, error, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  const [isLoadingOwner, setIsLoadingOwner] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    console.log("📝 Tentative de connexion:", { email: formData.email });

    try {
      const result = await dispatch(loginUser({ email: formData.email, password: formData.password, rememberMe: formData.rememberMe })).unwrap();
      console.log("✅ Connexion réussie:", result);

      if (result.token && result.OwnerId) {
        console.log("🔑 Token reçu:", result.token.substring(0, 20) + "...");
        console.log("👤 OwnerId reçu:", result.OwnerId);

        setIsLoadingOwner(true);
        try {
          await dispatch(encoursowner()).unwrap();
          console.log("✅ Owner chargé avec succès");

          setTimeout(() => {
            setShouldRedirect(true);
          }, 500);
        } catch (ownerError) {
          console.error(
            "❌ Erreur chargement owner (peut continuer avec cache):",
            ownerError
          );

          setTimeout(() => {
            setShouldRedirect(true);
          }, 500);
        } finally {
          setIsLoadingOwner(false);
        }
      } else {
        console.error("⚠️ Token ou OwnerId manquant dans la réponse");
      }
    } catch (err: any) {
      console.error("❌ Erreur de connexion:", err);
    }
  };

  if (shouldRedirect && isAuthenticated && user?.token) {
    console.log("🔄 Redirection vers dashboard");
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex bg-white dark:bg-gray-900">
      {/* Bloc gauche : formulaire */}
      <div className="flex flex-col justify-center w-full lg:w-1/2 px-6 sm:px-8 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Connexion
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Accédez à votre espace de gestion
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
                  placeholder="Adresse email"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Mot de passe
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
                  placeholder="Mot de passe"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                <input
                  id="remember-me"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                Se souvenir de moi (30 jours)
              </label>

              <Link
                to="/forgot-password"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                Mot de passe oublié ?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading || isLoadingOwner}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading || isLoadingOwner ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {isLoadingOwner
                    ? "Chargement des données..."
                    : "Connexion en cours..."}
                </span>
              ) : (
                "Se connecter"
              )}
            </button>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              Pas encore de compte ?{" "}
              <Link
                to="/signup"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                S'inscrire
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Bloc droit : branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-indigo-600 px-12">
        <div className="max-w-md text-white">
          <div className="mb-6">
            <div className="h-28 flex items-center">
              <img src="/images/logo.png" alt="Logo" className="h-24 w-auto object-contain" />
            </div>
          </div>

          <h1 className="text-4xl font-bold leading-tight">
            Gestion immobilière simplifiée
          </h1>

          <p className="mt-4 text-indigo-100 text-base leading-7">
            Centralisez vos biens, suivez vos locations et gérez votre activité
            en toute simplicité depuis une interface claire et moderne.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;